import React from "react";
import { connect, type ConnectedProps } from "react-redux";

import { RolePermissions } from "../../../enums";
import { type FilterFormPropsBase } from "../types/props";
import { FilterFormBuilder, type FilterItemBase } from "../FilterFormBuilder/FilterFormBuilder";
import { FilterViewTypes } from "../../../enums/filterViewTypes";
import { type RootState } from "../../../features/Application/globaltypes/redux";
import { isEmpty } from "lodash";

enum UsersFilterPropertyNames {
  departments = "department",
  jobTitles = "title",
  roles = "roleId",
  countries = "countries",
  createDate = "createDate",
  lastLoginDate = "lastLoginDate",
  showRoleFilter = "showRoleFilter",
}

export interface UserFilterFormProps extends FilterFormPropsBase<string>, PropsFromRedux {
  datePickersPosition: "top" | "bottom";
  departmentFilterDisabled?: boolean;
  jobTitleFilterDisabled?: boolean;
  showLastLoginFilter?: boolean;
}

export const UsersFilterForm: React.FC<UserFilterFormProps> = ({
  filterOptions,
  filter,
  updateFilter,
  userPermissions,
  datePickersPosition = "top",
  departmentFilterDisabled,
  jobTitleFilterDisabled,
  showLastLoginFilter,
}) => {
  const hasPermissionToShowRoleFilter = userPermissions.includes(RolePermissions.RolesView);
  const showRoleFilter = filterOptions.showRoleFilter === undefined || filterOptions.showRoleFilter;

  const filters: FilterItemBase<string>[] = [
    {
      type: FilterViewTypes.MultiSelectWithAddition,
      label: "Departments",
      items: filterOptions.departments,
      propertyName: UsersFilterPropertyNames.departments,
      placeholder: "All Departments",
      otherProps: { disabled: departmentFilterDisabled && isEmpty(filterOptions.departments) },
    },
    {
      type: FilterViewTypes.MultiSelectWithAddition,
      label: "Job Titles",
      items: filterOptions.jobTitles,
      propertyName: UsersFilterPropertyNames.jobTitles,
      placeholder: "All Job Titles",
      otherProps: { disabled: jobTitleFilterDisabled && isEmpty(filterOptions.jobTitles) },
    },
    {
      type: FilterViewTypes.MultiSelect,
      label: "Role",
      items: filterOptions.roles,
      propertyName: UsersFilterPropertyNames.roles,
      placeholder: "All",
      condition: () => {
        return !!showRoleFilter && hasPermissionToShowRoleFilter;
      },
      otherProps: { disabled: true },
      hidden: true,
    },
    {
      type: FilterViewTypes.MultiSelect,
      label: "Country",
      propertyName: UsersFilterPropertyNames.countries,
      placeholder: "All",
      otherProps: { disabled: true },
      hidden: true,
    },
    {
      type: FilterViewTypes.DateRange,
      label: "Added",
      propertyName: UsersFilterPropertyNames.createDate,
      otherProps: { verticalPosition: datePickersPosition, horizontalPositionSecondPicker: "left", disabled: true },
      hidden: true,
    },
    {
      type: FilterViewTypes.DateRange,
      label: "Last Login",
      propertyName: UsersFilterPropertyNames.lastLoginDate,
      otherProps: { disabled: false },
      hidden: !showLastLoginFilter,
    },
  ];

  return (
    <FilterFormBuilder filterOptions={filterOptions} filter={filter} updateFilter={updateFilter} items={filters} />
  );
};

const mapStateToProps = (state: RootState) => ({
  userPermissions: state.userProfile.permissions,
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(UsersFilterForm);
export default ConnectedComponent;
